import { logger } from '../lib/logger.js';

/**
 * Parchear una fila que se acaba de LEER (riesgo R-19).
 *
 * `data/normalizacion.ts` fija la politica ante una fila que no valida: se
 * registra y se devuelve tal cual. Descartarla perderia datos del usuario;
 * hacerla estallar deja la aplicacion inutilizable por una fila rara.
 *
 * Lo que no se reviso al escribirla es que las capas de encima la respetaran.
 * Media docena de operaciones hacen `esquema.parse({ ...filaLeida, cambio })`
 * para guardar un cambio pequeño —aprobar una decision, activar una semana,
 * marcar un item de la compra— y ahi `parse` SI estalla. No dejan la pantalla en
 * blanco (caen dentro de un `try` que informa) pero **bloquean la operacion sin
 * arreglar el dato**: la decision no se puede aprobar nunca, y el mensaje no
 * dice como salir de ahi.
 *
 * La distincion que hace falta no es «validar o no validar», es DE QUIEN es el
 * defecto:
 *
 * * Si la fila guardada ya estaba rota, el defecto es anterior y no es del
 *   usuario. Se registra y se guarda el cambio igual: la fila conserva la forma
 *   que ya tenia —y que el servidor acepto en su dia, porque de ahi vino— con
 *   un campo distinto. Negarse a aprobar una decision por una clave que el
 *   cliente no reconoce deja al usuario encerrado.
 * * Si la fila estaba bien y es el CAMBIO el que la rompe, el defecto es del
 *   codigo que lo hace, y entonces estallar es lo correcto: impide guardar
 *   basura que hemos generado nosotros. Esa guarda no se toca.
 *
 * La base de datos no valida `muscle_priorities` ni `volume_targets`
 * (migraciones 0009 y 0013), asi que una clave que este cliente no reconozca es
 * una fila legal en el servidor y llega aqui por sincronizacion. Ese caso tiene
 * que degradarse, no reventar.
 */

/**
 * El esquema, en su forma minima.
 *
 * No se tipa `parse` como `(v) => T`: si lo hiciera, TypeScript inferiria `T`
 * del esquema y no de la fila, y los llamadores que pasan un parche con la
 * forma `Omit<Fila, columnas de sincronizacion>` dejarian de encajar.
 */
interface EsquemaParcheable {
  safeParse: (valor: unknown) => { success: boolean; data?: unknown };
  parse: (valor: unknown) => unknown;
}

export function parchear<T extends object>(
  esquema: EsquemaParcheable,
  fila: T,
  /*
   * `NoInfer` para que la forma la fije la FILA y no el parche. Los llamadores
   * pasan parches con la forma `Omit<Fila, columnas de sincronizacion>`; sin
   * esto, TypeScript busca un tipo comun a los dos y se queda con las columnas
   * de sincronizacion, que es lo unico que comparten.
   */
  parche: NoInfer<Partial<T>>,
  contexto: { tabla: string; id: string },
): T {
  const candidato = { ...fila, ...parche };

  const conParche = esquema.safeParse(candidato);
  if (conParche.success) return conParche.data as T;

  // No valida. ¿Ya no validaba antes?
  if (esquema.safeParse(fila).success) {
    // La fila estaba bien: lo ha roto este cambio. Estallar es lo correcto, y
    // `parse` da el error con la ruta del campo, que es lo que hace falta para
    // arreglarlo.
    return esquema.parse(candidato) as T;
  }

  logger.warn('db', 'ROW_SHAPE_MISMATCH_PATCHED', 'Fila que ya no encajaba con su contrato', {
    tabla: contexto.tabla,
    id: contexto.id,
    // Se guarda el cambio de todas formas: bloquear la operacion no arregla la
    // fila y deja al usuario sin salida.
    campos: Object.keys(parche).join(', '),
  });
  return candidato;
}
